import { RestError } from '@azure/core-http';
import {
  AccessControlDto,
  CreateAccessControlDto,
  UpdateAccessControlDto
} from '@groundfloor-platform/mgmt-service-g-sdk';

import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useApi } from '../../core/api/api.context';
import { QueryKeys } from '../../core/queries/query-keys.constants';

export function useQueryGetAccessControl(accessControlID: string | undefined) {
  const api = useApi();

  return useQuery<AccessControlDto, RestError>(
    [QueryKeys.ACCESS_CONTROL, accessControlID],
    async () => {
      return await api.accessControlController.get(accessControlID!);
    },
    { enabled: !!accessControlID }
  );
}

export function useMutationCreateAccessControl() {
  const api = useApi();

  return useMutation<AccessControlDto, RestError, { dto: CreateAccessControlDto }>(async ({ dto }) => {
    return await api.accessControlController.create(dto);
  });
}

export function useMutationUpdateAccessControl() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<AccessControlDto, RestError, { accessControlID: string; dto: UpdateAccessControlDto }>(
    async ({ accessControlID, dto }) => {
      return await api.accessControlController.update(accessControlID, dto);
    },
    {
      onSuccess(_, { accessControlID }) {
        queryClient.invalidateQueries([QueryKeys.ACCESS_CONTROL, accessControlID]);
      }
    }
  );
}

export function useMutationAddAccessCodes() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<AccessControlDto, RestError, { accessControlID: string; codes: string[] }>(
    async ({ accessControlID, codes }) => {
      return await api.accessControlController.addCodes(accessControlID, codes);
    },
    {
      onSuccess(_, { accessControlID }) {
        queryClient.invalidateQueries([QueryKeys.ACCESS_CONTROL, accessControlID]);
      }
    }
  );
}

export function useMutationDeleteAccessControl() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, RestError, { accessControlID: string }>(
    async ({ accessControlID }) => {
      return await api.accessControlController.delete(accessControlID);
    },
    {
      onSuccess(_, { accessControlID }) {
        queryClient.invalidateQueries([QueryKeys.ACCESS_CONTROL, accessControlID]);
        queryClient.invalidateQueries(QueryKeys.SETTINGS);
        queryClient.invalidateQueries(QueryKeys.SETTINGS_CHILDREN);
      }
    }
  );
}

export function useMutationRemoveAccessCodes() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<AccessControlDto, RestError, { accessControlID: string; codes: string[] }>(
    async ({ accessControlID, codes }) => {
      return await api.accessControlController.removeCodes(accessControlID, codes);
    },
    {
      onSuccess(_, { accessControlID }) {
        queryClient.invalidateQueries([QueryKeys.ACCESS_CONTROL, accessControlID]);
      }
    }
  );
}
